import * as Yup from 'yup';

import { ReturnPayment, FormValuesItems } from './types';

/**
 * Validates that at least one item is selected when items are available.
 * Allows refund without selected items if there are payments and no available items.
 */
export const validateItemsSelection = (
  items: FormValuesItems[] | undefined,
  context: Yup.TestContext
): boolean | Yup.ValidationError => {
  const hasSelectedItems = items?.some((item) => item.selected) ?? false;

  // If items are selected, validation passes
  if (hasSelectedItems) {
    return true;
  }

  // No items selected - check if there are any available items
  const hasAvailableItems =
    items?.some((item) => item.availableQuantity > 0) ?? false;

  // If there are available items but none selected, require selection
  if (hasAvailableItems) {
    return false;
  }

  // No available items - check if there are payments to refund money only
  const { returnPayments } = context.parent;
  const hasPayments = returnPayments && returnPayments.length > 0;

  if (hasPayments) {
    return true;
  }

  return context.createError({
    message: 'No items available for refund and no payments added',
  });
};

/**
 * Validates that at least one payment is added when no items are selected.
 * Allows refund without payments if items are selected.
 */
export const validatePaymentsRequired = (
  returnPayments: ReturnPayment[] | undefined,
  context: Yup.TestContext
): boolean | Yup.ValidationError => {
  const hasPayments = (returnPayments?.length ?? 0) > 0;

  // If payments exist, they will be validated by validateReturnPayments
  if (hasPayments) {
    return true;
  }

  // No payments - check if there are selected items to allow item-only refund
  const { items } = context.parent;
  const hasSelectedItems =
    items?.some((item: FormValuesItems) => item.selected) ?? false;

  // If items are selected, payment is not required
  if (hasSelectedItems) {
    return true;
  }

  return context.createError({
    message: 'Either select items or add at least one payment method',
  });
};
